import {
  ChangeEvent,
  FormEventHandler,
  MouseEvent,
  SyntheticEvent,
  useEffect,
  useState,
} from "react";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { FormHelperText } from "@remo-co/ui-core/src/components/FormHelperText";
import { InputAdornment } from "@remo-co/ui-core/src/components/InputAdornment";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { Container } from "@remo-co/ui-core/src/components/Container";
import { FormTextInput } from "@remo-co/ui-core/src/components/FormTextInput";
import { useI18n } from "i18n";
import useAuth from "modules/auth/hooks/useAuth";
import { CheckCircle } from "@remo-co/ui-core/src/icons/CheckCircle";
import { useCheckEventStatus } from "modules/event/hooks/useCheckEventStatus";
import { isValidEmailAddress } from "helpers/emailHelper";
import { IEvent } from "modules/event/types";
import { checkIfEmailInvited } from "./utils";
import { useStyles } from "./SignIn.styles";

export interface SignInProps {
  event: IEvent;
  email: string;
  setEmail: (arg: string) => void;
  setIsSigningUp: (arg: boolean) => void;
  showFPForm: (e: MouseEvent<HTMLButtonElement>) => void;
}

const SignIn = ({
  event,
  email,
  setEmail,
  showFPForm,
  setIsSigningUp,
}: SignInProps) => {
  const styles = useStyles();
  const { t } = useI18n(["common", "eventLanding", "auth"]);
  const [passwordField, setPasswordField] = useState({
    error: false,
    value: "",
    message: "",
  });
  const { signIn } = useAuth();
  const { checkIfEventHappeningNow } = useCheckEventStatus();
  const isHappeningNow = checkIfEventHappeningNow(event);
  const [isInvited, setIsInvited] = useState(false);

  const handleFormSubmit: FormEventHandler<HTMLFormElement> = async (
    e: SyntheticEvent<HTMLFormElement>,
  ) => {
    e.preventDefault();
    e.stopPropagation();

    if (email && isValidEmailAddress(email)) {
      setIsSigningUp(true);
      const result = await signIn(email, passwordField.value);

      if (!result) {
        setIsSigningUp(false);
      }
    }
  };

  useEffect(() => {
    setPasswordField({
      value: passwordField.value,
      error: false,
      message: "",
    });
  }, [passwordField.value]);

  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPasswordField({ ...passwordField, value: e.target.value, message: "" });
  };

  const handleEmailBlur = async () => {
    checkIfEmailInvited(email, event, setIsInvited);
  };

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  let id;

  if (isHappeningNow) {
    id = "enter-password-modal_primary-cta-join-event-now";
  } else {
    id = "enter-password-modal_primary-cta-register-event";
  }

  return (
    <form
      data-testid="login-form"
      onSubmit={handleFormSubmit}
      className={styles.form}
    >
      <FormTextInput
        fullWidth
        autoFocus
        label={t("eventLanding:your.email")}
        InputLabelProps={{ shrink: true }}
        name="email"
        required
        type="email"
        placeholder={t("eventLanding:your.email")}
        value={email}
        onChange={handleEmailChange}
        onBlur={handleEmailBlur}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {event.isPrivate && isInvited && (
                <CheckCircle
                  data-testid="check-circle-icon"
                  className={styles.greenCheck}
                />
              )}
            </InputAdornment>
          ),
          classes: { root: styles.textFieldInput },
        }}
        classes={{ root: styles.textField }}
      />
      <Container className={styles.forgetPasswordContainer}>
        <Button
          data-testid="fp-button"
          onClick={showFPForm}
          size="xs"
          variant="ghost"
          color="blue"
          isIconButton
          className={styles.forgetPasswordButton}
        >
          <Typography className={styles.forgetPasswordText}>
            {t("eventLanding:forgot.password")}
          </Typography>
        </Button>
      </Container>
      <Container bottom={20}>
        <FormTextInput
          fullWidth
          label={t("eventLanding:password.password")}
          InputLabelProps={{ shrink: true }}
          name="password"
          placeholder={t("eventLanding:enter.your.password")}
          InputProps={{ classes: { root: styles.textFieldInput } }}
          onChange={handlePasswordChange}
          value={passwordField.value}
          type="password"
          classes={{ root: styles.textField }}
        />
        {passwordField.message && (
          <FormHelperText error id="password-text">
            {passwordField.message}
          </FormHelperText>
        )}
      </Container>
      <Button
        id={id}
        fullWidth
        type="submit"
        color="blue"
        variant="secondary"
        contentWrapperClassName={styles.buttonContent}
        className={styles.button}
        data-testid="login-button"
        disabled={passwordField.error || !passwordField.value}
      >
        {isHappeningNow
          ? t("eventLanding:event.join")
          : t("eventLanding:register.event")}
      </Button>
    </form>
  );
};

export default SignIn;
